import { useRef, useState } from "react"
import { useDrag, useDrop } from "react-dnd"
import { GripVertical, Pencil, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  checkNewStop,
  currentAddress,
  moveStop,
  otherRoutes,
  removeStop,
  renameStop,
  sameAddressStops,
  setAddress,
  type DailyRoutesDraft,
  type StopRef,
} from "@/lib/dailyRoutes/draft"
import { problem } from "./AddStopForm"
import { SameAddressDialog } from "./SameAddressDialog"
import { useTranslation } from "react-i18next"

type DragItem = { index: number }

type StopRowProps = {
  route: string
  draft: DailyRoutesDraft
  code: string
  index: number
  saving: boolean
  onChange: (next: DailyRoutesDraft) => void
}

type Question = { address: string; others: StopRef[] }

export function StopRow({
  route,
  draft,
  code,
  index,
  saving,
  onChange,
}: StopRowProps) {
  const { t } = useTranslation()
  const rowRef = useRef<HTMLLIElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)
  const current = currentAddress(draft, route, code) ?? ""
  const [value, setValue] = useState(current)
  const [seen, setSeen] = useState(current)
  const [error, setError] = useState<string | null>(null)
  const [question, setQuestion] = useState<Question | null>(null)
  // the new code while the name is being edited
  const [newCode, setNewCode] = useState<string | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  // mirrors `question` synchronously: the blur that follows the Enter press
  // (focus moving into the dialog) must not ask a second time, and the
  // dialog reports a dismissal after every button click as well
  const asking = useRef<Question | null>(null)

  // the draft changed under the input (saved, discarded, same-address edit)
  if (current !== seen) {
    setSeen(current)
    setValue(current)
  }

  const dragType = `daily-route-stop:${route}`
  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: dragType,
    canDrop: (item) => !saving && item.index !== index,
    drop: (item) => onChange(moveStop(draft, route, item.index, index)),
    collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
  })
  const [{ dragging }, drag, preview] = useDrag<
    DragItem,
    void,
    { dragging: boolean }
  >({
    type: dragType,
    item: { index },
    canDrag: () => !saving,
    collect: (monitor) => ({ dragging: monitor.isDragging() }),
  })
  drag(handleRef)
  preview(drop(rowRef))

  const answer = (apply: ((q: Question) => void) | null) => {
    const asked = asking.current
    if (!asked) return
    asking.current = null
    setQuestion(null)
    if (apply) apply(asked)
    else setValue(current)
  }

  const commit = () => {
    if (asking.current) return
    const address = value.trim()
    // nothing changed: no validation, no commit
    if (address === current.trim()) {
      setValue(current)
      return
    }
    if (!address) {
      setValue(current)
      setError(t("maps.stopRow.addressEmpty"))
      return
    }
    const others = sameAddressStops(draft, route, code)
    if (others.length === 0) {
      onChange(setAddress(draft, [{ route, code }], address))
      return
    }
    asking.current = { address, others }
    setQuestion(asking.current)
  }

  const stopRenaming = () => {
    setNewCode(null)
    setRenameError(null)
  }

  const commitRename = () => {
    if (newCode === null) return
    if (newCode.trim() === code.trim()) {
      stopRenaming()
      return
    }
    const check = checkNewStop(draft, route, newCode, index)
    const message = problem(t, check, newCode)
    if (message) {
      setRenameError(message)
      return
    }
    onChange(renameStop(draft, route, index, newCode))
    stopRenaming()
  }

  const alsoOn = otherRoutes(draft, code, route)
  return (
    <li
      ref={rowRef}
      className={cn(
        "flex flex-col gap-1 rounded-md border border-transparent px-1 py-1",
        isOver && "border-primary bg-muted",
        dragging && "opacity-40",
      )}
    >
      <div className="flex items-center gap-2">
        <span
          ref={handleRef}
          aria-label={t("maps.stopRow.drag", { code })}
          aria-disabled={saving}
          className={cn(
            "shrink-0 text-muted-foreground",
            saving ? "cursor-not-allowed opacity-50" : "cursor-grab",
          )}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </span>
        <span className="w-6 shrink-0 text-right text-xs text-muted-foreground">
          {index + 1}
        </span>
        {newCode === null ? (
          <span className="flex w-60 shrink-0 items-center gap-1">
            <span className="min-w-0 flex-1 break-words text-sm font-medium">
              {code}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t("maps.stopRow.rename", { code })}
              disabled={saving}
              className="shrink-0 text-muted-foreground"
              onClick={() => setNewCode(code)}
            >
              <Pencil aria-hidden />
            </Button>
          </span>
        ) : (
          <Input
            aria-label={t("maps.stopRow.newCode", { code })}
            className="w-60 shrink-0"
            autoFocus
            disabled={saving}
            value={newCode}
            onChange={(event) => {
              setNewCode(event.target.value)
              setRenameError(null)
            }}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                commitRename()
              } else if (event.key === "Escape") {
                stopRenaming()
              }
            }}
          />
        )}
        {alsoOn.length > 0 && (
          <Badge
            variant="neutral"
            className="shrink-0"
            title={t("maps.stopRow.alsoOnTitle")}
          >
            {t("maps.stopRow.alsoOn", { routes: alsoOn.join(", ") })}
          </Badge>
        )}
        <Input
          aria-label={t("maps.stopRow.addressOf", { code })}
          className="min-w-0 flex-1"
          disabled={saving}
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
            setError(null)
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              commit()
            } else if (event.key === "Escape") {
              setValue(current)
              setError(null)
            }
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("maps.stopRow.remove", { code })}
          disabled={saving}
          onClick={() => onChange(removeStop(draft, route, index))}
        >
          <X aria-hidden />
        </Button>
      </div>
      {error && (
        <p role="alert" className="pl-10 text-xs text-destructive">
          {error}
        </p>
      )}
      {renameError && (
        <p role="alert" className="pl-10 text-xs text-destructive">
          {renameError}
        </p>
      )}
      {question && (
        <SameAddressDialog
          route={route}
          code={code}
          others={question.others}
          onChangeAll={() =>
            answer((q) =>
              onChange(
                setAddress(draft, [{ route, code }, ...q.others], q.address),
              ),
            )
          }
          onOnlyThis={() =>
            answer((q) =>
              onChange(setAddress(draft, [{ route, code }], q.address)),
            )
          }
          onCancel={() => answer(null)}
        />
      )}
    </li>
  )
}
